import { api } from '@api'
import type { Tables } from '@hvf/contracts'

export const PAGE_SIZE = 10

export type DiscoverySource = 'event' | 'manual' | 'general'

// Define the specific shape of your joined data
export type RecordingWithHost = Tables<'recordings'> & {
	host:
		| (Pick<Tables<'profile'>, 'id' | 'username'> & {
				profile_private: Pick<Tables<'profile_private'>, 'role'> | null
		  })
		| null
	events: { group: string } | null
}

export const getRecordings = async (
	filterValue: string,
	sourceType: DiscoverySource,
	page = 0,
): Promise<{ data: RecordingWithHost[]; count: number }> => {
	const from = page * PAGE_SIZE
	const to = from + PAGE_SIZE - 1

	// Use the alias 'host:hosted_by' to rename the property in the response
	const profileFields = `
    host:hosted_by (
      id,
      username,
      profile_private (
        role
      )
    )
  `

	// Initialize query with PostgrestFilterBuilder type for better intellisense
	let query: any

	if (sourceType === 'event') {
		query = api()
			.from('recordings')
			.select(`*, events!inner(group), ${profileFields}`, { count: 'exact' })
			.eq('events.group', filterValue)
	} else if (sourceType === 'manual') {
		query = api()
			.from('recordings')
			.select(`*, events!left(group), ${profileFields}`, { count: 'exact' })
			.eq('group', filterValue)
			.is('events.group', null)
	} else {
		query = api()
			.from('recordings')
			.select(`*, events!left(group), ${profileFields}`, { count: 'exact' })
			.is('group', null)
			.or('group.is.null,group.eq.""', { foreignTable: 'events' })
	}

	const { data, error, count } = await query
		.order('occurrence_date', { ascending: false })
		.range(from, to)

	if (error) throw new Error(error.message)

	// Cast the data to our joined type
	return {
		data: (data as RecordingWithHost[]) || [],
		count: count ?? 0,
	}
}

export const deleteRecording = async (id: string) => {
	const { error } = await api().from('recordings').delete().eq('id', id)

	if (error) throw new Error(error.message)
	return true
}

export type RecordingSearchParams = {
	search: string
	/** `${source_type}:${filter_value}` of a discovery row */
	group: string | null
	/** ISO date; only recordings that happened on or after it */
	since: string | null
	evergreenOnly: boolean
	ascending: boolean
}

/** Flat, filtered list of recordings across every group — used by the Recordings search/filters */
export const searchRecordings = async (
	params: RecordingSearchParams,
	page = 0,
): Promise<{ data: RecordingWithHost[]; count: number }> => {
	const from = page * PAGE_SIZE
	const to = from + PAGE_SIZE - 1

	const [sourceType, ...rest] = params.group?.split(':') ?? []
	const groupValue = rest.join(':')
	const eventJoin = sourceType === 'event' ? 'events!inner(group)' : 'events!left(group)'

	let query: any = api()
		.from('recordings')
		.select(
			`*, ${eventJoin}, host:hosted_by ( id, username, profile_private ( role ) )`,
			{ count: 'exact' },
		)

	const term = params.search.trim().replace(/["\\]/g, '')
	if (term) {
		query = query.or(
			['title', 'description', 'group'].map((c) => `${c}.ilike."%${term}%"`).join(','),
		)
	}

	if (sourceType === 'event') query = query.eq('events.group', groupValue)
	else if (sourceType === 'manual') query = query.eq('group', groupValue).is('event_id', null)
	else if (sourceType === 'general') query = query.is('group', null).is('event_id', null)

	if (params.since) query = query.gte('occurrence_date', params.since)
	if (params.evergreenOnly) query = query.eq('evergreen', true)

	const { data, error, count } = await query
		.order('occurrence_date', { ascending: params.ascending })
		.range(from, to)

	if (error) throw new Error(error.message)

	return {
		data: (data as RecordingWithHost[]) || [],
		count: count ?? 0,
	}
}
